import { useCallback, useEffect } from "react"

import { toggleSelection } from "./elementData"
import {
  useElementBase,
  ElementAdapter,
  ElementBaseContext,
} from "./useElementBase"
import { useTimeSeriesConfig } from "./useElementConfigFactory"
import { useOperatorManagement } from "../useOperatorManagement"

import {
  DEFAULT_TIMESERIES_WIDTH,
  DEFAULT_TIMESERIES_HEIGHT,
} from "constants/dashboardElementSizes"

import { convertEngineOutputToTimeSeries } from "utils/convertEngineOutputToTimeSeries"
import { ScriptGenerationService } from "services/ScriptGenerationService"
import { generateChartImageFromProcessedData } from "services/ChartImageService"

import {
  STATISTIC_TYPE,
  ELEMENT_TYPE,
  SlideElement,
  DataElementConfig,
  TimeSeriesChartSettings,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { ElementRef } from "../../../../features/Reporting/components/ReportingDisplay/ReportingDisplay.component"
import {
  TimeSeriesRawDataPayload,
  ConvertedTimeSeriesData,
} from "types/ReportingDashboard/timeseries.types"

/**
 * Empty converted-view value. The data slot is never null in this hook: it
 * starts (and is reset) to this empty view object, which — being truthy —
 * also keeps useElementState's internal auto-run permanently disarmed
 * (the base's hook-level auto-run effect is the single owner of auto_run).
 */
const EMPTY_TIME_SERIES_DATA: ConvertedTimeSeriesData = {
  lineChartNivoData: [],
  seriesKeys: [],
  waveIdentifiers: [],
}

/**
 * Element update payload for time series chart elements
 */
interface TimeSeriesElementUpdate {
  width?: number
  height?: number
}

/**
 * Props for useTimeSeriesChartElement hook
 *
 * NOTE: no `filter` prop — DECIDED (2026-07-02, risk item 7): for time
 * series the wave variable IS the filter dimension, so the global dashboard
 * filter intentionally does not apply (reactsToFilter: false on the adapter).
 */
interface UseTimeSeriesChartElementProps {
  /** Element object to render */
  element: SlideElement
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Callback when element properties change */
  onUpdate: (elementId: string, updates: TimeSeriesElementUpdate) => void
  /** Configuration for time series chart element */
  config?: DataElementConfig | null
  /** Callback for config updates */
  onConfigUpdate: (config: DataElementConfig | null) => void
  /** Whether in dashboard mode */
  isDashboard?: boolean
  /** Callback when element deletion is requested */
  onDeleteRequest?: () => void
  /** Sequential execution request handler */
  onElementExecutionRequest?: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void
  /** Element registration handler */
  onRegisterElementRef?: (elementId: string, ref: ElementRef | null) => void
}

type TimeSeriesContext = ElementBaseContext<ConvertedTimeSeriesData>

const getTimeSeriesVariables = (
  ctx: TimeSeriesContext
): {
  primaryVar: string | null
  breakdownVar: string | null
  waveVar: string | null
} => ({
  primaryVar: ctx.getValue<string | null>("selectedPrimaryVariable") ?? null,
  breakdownVar:
    ctx.getValue<string | null>("selectedBreakdownVariable") ?? null,
  waveVar: ctx.getValue<string | null>("selectedWaveVariable") ?? null,
})

/**
 * Time series adapter for useElementBase.
 *
 * Pinned TS-specific behavior preserved here:
 * - The data slot holds the CONVERTED view object (never null); config loads
 *   reset it via updateElementState keeping hasProcessedData FALSE (unlike
 *   the siblings' setData(null) disarm — the internal auto-run is disabled
 *   outright instead).
 * - Without an onElementExecutionRequest parent, execution requests fall
 *   back to a DIRECT execution (the siblings silently no-op).
 * - The wave variable is passed through generateTimeSeriesScript's `filter`
 *   parameter slot, with a ts_-prefixed table id.
 */
const timeSeriesAdapter: ElementAdapter<ConvertedTimeSeriesData> = {
  elementType: ELEMENT_TYPE.TIME_SERIES_CHART,
  isTimeSeries: true,
  useConfigHook: useTimeSeriesConfig,
  defaultSize: {
    width: DEFAULT_TIMESERIES_WIDTH,
    height: DEFAULT_TIMESERIES_HEIGHT,
  },
  reactsToFilter: false,
  directExecuteWhenUnmanaged: true,
  initialData: EMPTY_TIME_SERIES_DATA,
  internalAutoRunEnabled: false,
  convertsRTableData: false,
  configShowsData: (config): boolean => {
    const timeSeriesConfig = config as TimeSeriesChartSettings
    return !!(
      timeSeriesConfig.selectedPrimaryVariable &&
      timeSeriesConfig.selectedBreakdownVariable &&
      timeSeriesConfig.selectedWaveVariable
    )
  },
  resetDataForConfigLoad: (ctx): void => {
    ctx.updateElementState({
      data: EMPTY_TIME_SERIES_DATA,
      hasProcessedData: false,
    })
  },
  hasRequiredSelections: (ctx): boolean => {
    const { primaryVar, breakdownVar, waveVar } = getTimeSeriesVariables(ctx)
    return !!(primaryVar && breakdownVar && waveVar)
  },
  autoRunSignature: (ctx): string => {
    const { primaryVar, breakdownVar, waveVar } = getTimeSeriesVariables(ctx)
    return `${primaryVar}|${breakdownVar}|${waveVar}`
  },
  isDataEmptyForAutoRun: (data): boolean =>
    (data ?? EMPTY_TIME_SERIES_DATA).lineChartNivoData.length === 0,
  hasDataAndShouldRefresh: (ctx): boolean => {
    const { primaryVar, breakdownVar, waveVar } = getTimeSeriesVariables(ctx)
    return !!(
      primaryVar &&
      breakdownVar &&
      waveVar &&
      (ctx.data ?? EMPTY_TIME_SERIES_DATA).lineChartNivoData.length > 0
    )
  },
  // Main script execution function
  execute: async (ctx): Promise<void> => {
    const timeSeriesConfig = ctx.props.config as TimeSeriesChartSettings
    const {
      primaryVar: selectedPrimaryVariable,
      breakdownVar: selectedBreakdownVariable,
      waveVar: selectedWaveVariable,
    } = getTimeSeriesVariables(ctx)
    const primaryVar =
      selectedPrimaryVariable ?? timeSeriesConfig?.selectedPrimaryVariable
    const breakdownVar =
      selectedBreakdownVariable ?? timeSeriesConfig?.selectedBreakdownVariable
    const waveVar =
      selectedWaveVariable ?? timeSeriesConfig?.selectedWaveVariable
    const statType =
      ctx.getValue<STATISTIC_TYPE>("selectedStatisticType", STATISTIC_TYPE.N) ??
      timeSeriesConfig?.selectedStatisticType ??
      STATISTIC_TYPE.N
    const operationType =
      ctx.getValue<TABLE_OPERATION_TYPE>(
        "tableOperationType",
        TABLE_OPERATION_TYPE.XTAB
      ) ??
      timeSeriesConfig?.tableOperationType ??
      TABLE_OPERATION_TYPE.XTAB
    const selectedDashboardSeries = ctx.getValue<string[]>(
      "selectedDashboardSeries",
      []
    )
    const selectedDashboardWaves = ctx.getValue<string[]>(
      "selectedDashboardWaves",
      []
    )

    if (!primaryVar || !breakdownVar || !waveVar) {
      console.warn("All three variables are required for TimeSeries chart")
      return
    }
    if (ctx.isLoading) {
      console.warn(
        "TimeSeriesChartElement: executeTimeSeriesScript aborted: Already running"
      )
      return
    }

    ctx.clearError()
    ctx.markAttemptedLoad()

    try {
      // Generate script using the centralized service. The wave variable is
      // passed through the `filter` parameter slot (it IS the filter
      // dimension for time series), with a ts_-prefixed table id.
      const script = ScriptGenerationService.generateTimeSeriesScript(
        `ts_${ctx.element.id}`,
        primaryVar,
        breakdownVar,
        statType,
        operationType,
        waveVar
      )

      const result = await ctx.executeScript(script, ctx.element.id)

      if (result.success && Array.isArray(result.data)) {
        const rawFullTableResultsArray = result.data as TimeSeriesRawDataPayload
        const { lineChartNivoData, seriesKeys, waveIdentifiers } =
          convertEngineOutputToTimeSeries(
            rawFullTableResultsArray,
            statType,
            primaryVar,
            breakdownVar
          )

        const hasValidData =
          lineChartNivoData.length > 0 &&
          lineChartNivoData.some((series) => series.data.length > 0)

        // setData also marks hasProcessedData true (same as before)
        ctx.setData({
          lineChartNivoData,
          seriesKeys,
          waveIdentifiers,
        })

        if (hasValidData) {
          ctx.updateConfigState({ showSelects: false })

          if (ctx.isDashboard) {
            const initialSeries =
              selectedDashboardSeries?.length > 0
                ? selectedDashboardSeries.filter((s) => seriesKeys.includes(s))
                : seriesKeys
            const finalSeries =
              initialSeries.length > 0 ? initialSeries : seriesKeys

            const initialWaves =
              selectedDashboardWaves?.length > 0
                ? selectedDashboardWaves.filter((w) =>
                    waveIdentifiers.includes(w)
                  )
                : waveIdentifiers
            const finalWaves =
              initialWaves.length > 0 ? initialWaves : waveIdentifiers

            ctx.configHook.setConfigValue(
              "selectedDashboardSeries",
              finalSeries
            )
            ctx.configHook.setConfigValue("selectedDashboardWaves", finalWaves)
          }
        }
      }
    } catch (e) {
      console.error(
        `Script execution error for TimeSeries element ${ctx.element.id}:`,
        e
      )
    }
  },
  resetState: (ctx): void => {
    ctx.resetElement()
    // resetElement nulls the data slot; restore the non-null empty view
    // (batched with the reset, so no intermediate null render).
    ctx.updateElementState({ data: EMPTY_TIME_SERIES_DATA })
    ctx.clearError()
  },
  onBeforeRefresh: (ctx): void => {
    ctx.updateElementState({ hasProcessedData: false })
  },
}

/**
 * Custom Hook: useTimeSeriesChartElement
 *
 * Time series chart element business logic, composed from useElementBase
 * (shared lifecycle: data loading, config wiring, element state, auto-run
 * and registration effects, refresh/edit/delete family — with the TS
 * direct-execute queue fallback and reactsToFilter: false) plus the time
 * series adapter (wave-array execution and conversion, series/wave seeding)
 * and TS-only extras: handleGenerate, series/wave toggles, the image effect
 * (which persists the config on the user_generate path — risk item 9
 * behavior intentionally unchanged), formatYTickForLineChart, waveVarName
 * and lineChartHeight.
 *
 * @param props - Time series chart element configuration and callbacks
 * @returns Object containing all state, handlers, and configuration needed by component
 */
export const useTimeSeriesChartElement = (
  props: UseTimeSeriesChartElementProps
) => {
  const { element, config } = props

  const base = useElementBase<ConvertedTimeSeriesData>(
    props,
    timeSeriesAdapter
  )

  const {
    isLoading,
    error,
    configHook,
    data: rawTimeSeriesChartData,
    showSelects,
    hasAttemptedLoad,
    hasProcessedData,
    isQueuedForExecution,
    selectOptions,
    requestSequentialExecution,
    hasDataAndShouldRefresh,
    handleRefresh,
    handleEdit,
    handleDelete,
  } = base

  // The converted view is never null in practice; normalize for type safety.
  const timeSeriesChartData = rawTimeSeriesChartData ?? EMPTY_TIME_SERIES_DATA

  // Get values from config with fallbacks
  const selectedPrimaryVariable = configHook.getConfigValue<string | null>(
    "selectedPrimaryVariable"
  )
  const selectedBreakdownVariable = configHook.getConfigValue<string | null>(
    "selectedBreakdownVariable"
  )
  const selectedWaveVariable = configHook.getConfigValue<string | null>(
    "selectedWaveVariable"
  )
  const selectedStatisticType = configHook.getConfigValue<STATISTIC_TYPE>(
    "selectedStatisticType",
    STATISTIC_TYPE.N
  )
  const selectedDashboardSeries = configHook.getConfigValue<string[]>(
    "selectedDashboardSeries",
    []
  )
  const selectedDashboardWaves = configHook.getConfigValue<string[]>(
    "selectedDashboardWaves",
    []
  )
  const tableOperationType = configHook.getConfigValue<TABLE_OPERATION_TYPE>(
    "tableOperationType",
    TABLE_OPERATION_TYPE.XTAB
  )

  // Use operator management hook for centralized operator handling
  const operatorManagement = useOperatorManagement({
    initialOperationType: tableOperationType,
    initialStatisticType: selectedStatisticType,
    singleStatisticMode: true, // Time series uses single statistic selection
    onOperationTypeChange: (newOperationType) => {
      configHook.setConfigValue("tableOperationType", newOperationType)
    },
    onStatisticTypeChange: (newStatisticType) => {
      configHook.setConfigValue("selectedStatisticType", newStatisticType)
    },
  })

  // Handlers
  const handleVariableSelect = useCallback(
    (value: string, type: "primary" | "breakdown" | "wave"): void => {
      if (type === "primary") {
        configHook.setConfigValue("selectedPrimaryVariable", value)
      } else if (type === "breakdown") {
        configHook.setConfigValue("selectedBreakdownVariable", value)
      } else if (type === "wave") {
        configHook.setConfigValue("selectedWaveVariable", value)
      }
    },
    [configHook]
  )

  const handleGenerate = useCallback((): void => {
    if (
      selectedPrimaryVariable &&
      selectedBreakdownVariable &&
      selectedWaveVariable
    ) {
      requestSequentialExecution("user_generate")
    }
  }, [
    selectedPrimaryVariable,
    selectedBreakdownVariable,
    selectedWaveVariable,
    requestSequentialExecution,
  ])

  // Dashboard filter handlers
  const handleSeriesToggle = useCallback(
    (series: string): void => {
      configHook.setConfigValue(
        "selectedDashboardSeries",
        toggleSelection(selectedDashboardSeries, series)
      )
    },
    [selectedDashboardSeries, configHook]
  )

  const handleWaveToggle = useCallback(
    (wave: string): void => {
      configHook.setConfigValue(
        "selectedDashboardWaves",
        toggleSelection(selectedDashboardWaves, wave)
      )
    },
    [selectedDashboardWaves, configHook]
  )

  // Image generation effect (for export functionality)
  /**
   * NOTE (risk items 8 and 9, intentionally unchanged): unlike Chart, this
   * effect has no config-string dedup guard, and config persistence rides
   * it (configHook.saveConfig) — on the user_generate path no skip flag was
   * set, so the config IS saved with the fresh chartDataUri attached.
   */
  useEffect(() => {
    if (
      !timeSeriesChartData.lineChartNivoData.length ||
      showSelects ||
      configHook.isGeneratingImageRef.current
    ) {
      return
    }

    const timer = setTimeout(async () => {
      if (configHook.isGeneratingImageRef.current) return

      try {
        configHook.isGeneratingImageRef.current = true

        const dataUri = await generateChartImageFromProcessedData(
          ELEMENT_TYPE.TIME_SERIES_CHART,
          timeSeriesChartData.lineChartNivoData,
          {
            ...((config as TimeSeriesChartSettings) || {}),
            width: element.width || DEFAULT_TIMESERIES_WIDTH,
            height: element.height || DEFAULT_TIMESERIES_HEIGHT,
          }
        )

        configHook.saveConfig({
          ...(configHook.config as TimeSeriesChartSettings),
          chartDataUri: dataUri,
        })
      } catch (error) {
        console.error("Error generating time series chart image:", error)
        configHook.saveConfig({
          ...(configHook.config as TimeSeriesChartSettings),
          chartDataUri: null,
        })
      } finally {
        setTimeout(() => {
          configHook.isGeneratingImageRef.current = false
        }, 100)
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [
    timeSeriesChartData.lineChartNivoData,
    showSelects,
    selectedDashboardSeries,
    selectedDashboardWaves,
    element.width,
    element.height,
    config,
    configHook,
  ])

  // Y-axis tick formatter
  const formatYTickForLineChart = useCallback(
    (value: unknown): string => {
      const statType = selectedStatisticType || STATISTIC_TYPE.N
      if (String(statType).includes("%") || String(statType).includes("perc")) {
        const num = parseFloat(String(value))
        return isNaN(num) ? String(value) : `${(num * 100).toFixed(1)}%`
      }
      const num = parseFloat(String(value))
      return isNaN(num)
        ? String(value)
        : Number.isInteger(num)
        ? num.toLocaleString()
        : num.toFixed(2)
    },
    [selectedStatisticType]
  )

  // Derived values
  const waveVarName = selectedWaveVariable?.split("//").pop() || "Waves"
  const renderHeight = element.height || DEFAULT_TIMESERIES_HEIGHT
  const topBottomPaddingRem = 0.5
  const chartAreaHeight = showSelects
    ? 0
    : renderHeight - topBottomPaddingRem * 2 * 16
  const lineChartHeight = Math.max(150, chartAreaHeight - 30)

  // Return all state, handlers, and configuration needed by the component.
  return {
    // State
    timeSeriesChartData,
    showSelects,
    isLoading,
    isQueuedForExecution,
    error,
    hasAttemptedLoad,
    hasProcessedData,

    // Config values
    selectedPrimaryVariable,
    selectedBreakdownVariable,
    selectedWaveVariable,
    selectedStatisticType,
    selectedDashboardSeries,
    selectedDashboardWaves,
    tableOperationType,

    // Hooks
    configHook,
    operatorManagement,

    // Handlers
    handleRefresh,
    handleEdit,
    handleDelete,
    handleVariableSelect,
    handleGenerate,
    handleSeriesToggle,
    handleWaveToggle,
    requestSequentialExecution,

    // Imperative handle values
    hasDataAndShouldRefresh,

    // Computed values
    selectOptions,
    formatYTickForLineChart,
    waveVarName,
    lineChartHeight,
  }
}
